/**
 * Product Addons Component
 * Handles accessory, personalization, and gender-based size add-to-cart functionality
 */
class ProductAddons extends HTMLElement {
  // Size data for each gender
  static SIZES = {
    Female: [
      { eu: '35', uk: '3' },
      { eu: '36', uk: '4' },
      { eu: '37', uk: '4.5' },
      { eu: '38', uk: '5' },
      { eu: '39', uk: '6' },
      { eu: '40', uk: '6.5' },
      { eu: '41', uk: '7' },
      { eu: '42', uk: '7.5' },
      { eu: '43', uk: '9' }
    ],
    Male: [
      { eu: '41', uk: '7' },
      { eu: '42', uk: '7.5-8' },
      { eu: '43', uk: '9' },
      { eu: '44', uk: '10' },
      { eu: '45', uk: '11' },
      { eu: '46', uk: '11.5' },
      { eu: '47', uk: '12' },
      { eu: '48', uk: '12.5' },
      { eu: '49', uk: '13', customOrder: true }
    ]
  };

  constructor() {
    super();
    this.addonCheckbox = null;
    this.personalizationCheckbox = null;
    this.leftBootInput = null;
    this.rightBootInput = null;
    this.productForm = null;
    this.form = null;
    this.submitButton = null;
    this.formObserver = null;
    this.productFormObserver = null;
    this.personalizationProduct = null;
    this.personalizationVariantId = null;
    this.selectedGender = null;
    this.selectedSize = null;
    this.formValidationInput = null;
    this.formPropertyInputs = {
      gender: null,
      size: null,
      customOrder: null
    };
    this.pendingAddonItems = null;
    this.pendingAddonVariantId = null;
    this.pendingAddonTimeout = null;
    this.isAddingAddons = false;
    this.variantUpdateTarget = null;
    this.boundHandleValidationInvalid = this.handleValidationInvalid.bind(this);

    // Bind event handlers once so they can be properly removed
    this.boundHandlePersonalizationToggle = this.handlePersonalizationToggle.bind(this);
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
    this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
    this.boundHandleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.boundClearLeftInputError = () => {
      this.leftBootInput?.classList.remove('product-addon__text-input--error');
      this.updateFormValidity();
    };
    this.boundClearRightInputError = () => {
      this.rightBootInput?.classList.remove('product-addon__text-input--error');
      this.updateFormValidity();
    };
  }

  connectedCallback() {
    this.addonCheckbox = this.querySelector('[name="addon_selected"]');
    this.personalizationCheckbox = this.querySelector('[name="personalization_selected"]');
    this.leftBootInput = this.querySelector('[name="left_boot_text"]');
    this.rightBootInput = this.querySelector('[name="right_boot_text"]');
    this.personalizationDetails = this.querySelector('[data-personalization-details]');

    // Gender/Size selector elements
    this.genderRadios = this.querySelectorAll('[name="size_gender"]');
    this.sizeOptionsContainer = this.querySelector('[data-size-options]');
    this.sizeGrid = this.querySelector('[data-size-grid]');
    this.sizeError = this.querySelector('[data-size-error]');
    this.sizeErrorMessage = this.querySelector('[data-error-message]');
    this.sizeValidationInput = this.querySelector('[data-size-validation]');

    // Personalization error elements
    this.personalizationError = this.querySelector('[data-personalization-error]');
    this.personalizationErrorMessage = this.querySelector('[data-personalization-error-message]');

    // Cart error elements
    this.cartError = this.querySelector('[data-cart-error]');
    this.cartErrorMessage = this.querySelector('[data-cart-error-message]');

    // Set initial validation state (invalid until size is selected)
    this.updateFormValidity();

    // Set up gender selection handler
    this.genderRadios.forEach(radio => {
      radio.addEventListener('change', this.handleGenderChange.bind(this));
    });

    // Set up personalization checkbox toggle
    if (this.personalizationCheckbox) {
      this.personalizationCheckbox.addEventListener('change', this.boundHandlePersonalizationToggle);

      // Clear error styling on input
      this.leftBootInput?.addEventListener('input', this.boundClearLeftInputError);
      this.rightBootInput?.addEventListener('input', this.boundClearRightInputError);

      // Fetch personalization product details
      this.fetchPersonalizationProduct();
    }

    // Bind to product form using multiple strategies for reliability
    // (product-form-component renders after product-addons in block order)
    this.initFormBinding();

    const cartUpdateEvent = typeof ThemeEvents !== 'undefined' ? ThemeEvents.cartUpdate : 'cart:update';
    document.addEventListener(cartUpdateEvent, this.boundHandleCartUpdate);

    const variantUpdateEvent = typeof ThemeEvents !== 'undefined' ? ThemeEvents.variantUpdate : 'variant:update';
    this.variantUpdateTarget = this.closest('.shopify-section, dialog, product-card') || document;
    this.variantUpdateTarget.addEventListener(variantUpdateEvent, this.boundHandleVariantUpdate);
  }

  /**
   * Initialize form binding with multiple fallback strategies
   * This ensures we catch the product-form-component regardless of render timing
   */
  initFormBinding() {
    console.log('[ProductAddons] initFormBinding called');

    // Strategy 1: Try immediately (might already exist)
    if (this.tryBindToProductForm()) {
      console.log('[ProductAddons] Form bound immediately');
    }
    console.log('[ProductAddons] Form not found immediately, setting up observers');

    // Strategy 2: Use MutationObserver to watch for the element
    this.formObserver = new MutationObserver((mutations, observer) => {
      if (this.tryBindToProductForm()) {
        console.log('[ProductAddons] Form bound via MutationObserver');
        observer.disconnect();
        this.formObserver = null;
      }
    });

    this.formObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Strategy 3: Polling fallback with increasing delays
    // Covers edge cases where MutationObserver might miss the element
    const delays = [0, 10, 50, 100, 250, 500, 1000];
    delays.forEach(delay => {
      setTimeout(() => {
        if (!this.productForm && this.tryBindToProductForm()) {
          console.log(`[ProductAddons] Form bound via polling after ${delay}ms`);
          if (this.formObserver) {
            this.formObserver.disconnect();
            this.formObserver = null;
          }
        }
      }, delay);
    });

    // Strategy 4: Periodic rebind in case the theme replaces the form
    // (e.g. variant change re-renders product-form-component)
    this.rebindInterval = window.setInterval(() => {
      this.tryBindToProductForm();
    }, 1000);
  }

  /**
   * Attempt to bind to the product form
   * @returns {boolean} true if successfully bound, false otherwise
   */
  tryBindToProductForm() {
    // Find the product form
    const nextProductForm = this.closest('product-form-component') || document.querySelector('product-form-component');

    if (!nextProductForm) {
      return false;
    }

    if (this.productForm && this.productForm !== nextProductForm) {
      this.productForm.removeEventListener('submit', this.boundHandleFormSubmit, { capture: true });
    }

    this.productForm = nextProductForm;

    this.form = this.productForm.querySelector('form');
    this.productForm.addEventListener('submit', this.boundHandleFormSubmit, { capture: true });
    console.log('[ProductAddons] Successfully bound to product form events');

    if (!this.productFormObserver) {
      this.productFormObserver = new MutationObserver(() => {
        this.form = this.productForm?.querySelector('form') || null;
        this.ensureFormInputs();
        this.syncFormProperties();
        this.updateFormValidity();
      });
      this.productFormObserver.observe(this.productForm, { childList: true, subtree: true });
    }

    this.ensureFormInputs();
    this.syncFormProperties();
    this.updateFormValidity();

    return true;
  }

  disconnectedCallback() {
    // Clean up MutationObserver if still active
    if (this.formObserver) {
      this.formObserver.disconnect();
      this.formObserver = null;
    }
    if (this.productFormObserver) {
      this.productFormObserver.disconnect();
      this.productFormObserver = null;
    }
    if (this.rebindInterval) {
      window.clearInterval(this.rebindInterval);
      this.rebindInterval = null;
    }
    if (this.pendingAddonTimeout) {
      clearTimeout(this.pendingAddonTimeout);
      this.pendingAddonTimeout = null;
    }
    if (this.personalizationCheckbox) {
      this.personalizationCheckbox.removeEventListener('change', this.boundHandlePersonalizationToggle);
    }
    this.leftBootInput?.removeEventListener('input', this.boundClearLeftInputError);
    this.rightBootInput?.removeEventListener('input', this.boundClearRightInputError);
    if (this.formValidationInput) {
      this.formValidationInput.removeEventListener('invalid', this.boundHandleValidationInvalid);
    }
    if (this.productForm) {
      this.productForm.removeEventListener('submit', this.boundHandleFormSubmit, { capture: true });
    }
    const cartUpdateEvent = typeof ThemeEvents !== 'undefined' ? ThemeEvents.cartUpdate : 'cart:update';
    document.removeEventListener(cartUpdateEvent, this.boundHandleCartUpdate);

    const variantUpdateEvent = typeof ThemeEvents !== 'undefined' ? ThemeEvents.variantUpdate : 'variant:update';
    if (this.variantUpdateTarget) {
      this.variantUpdateTarget.removeEventListener(variantUpdateEvent, this.boundHandleVariantUpdate);
      this.variantUpdateTarget = null;
    }
  }

  /**
   * Fetch personalization product details via AJAX
   * Uses the known handle for direct fetch (most reliable)
   */
  async fetchPersonalizationProduct() {
    // Direct fetch using known handle - most reliable method
    const knownHandle = 'personalisation-15-r300';

    try {
      const response = await fetch(`/products/${knownHandle}.js`);
      if (response.ok) {
        this.personalizationProduct = await response.json();
        this.updatePersonalizationUI();
        return;
      }
    } catch (error) {
      console.warn('ProductAddons: Direct handle fetch failed, trying fallbacks');
    }

    // Fallback: try other common handles
    await this.fetchPersonalizationProductByHandle();
  }

  /**
   * Fallback method to fetch product by common handles
   */
  async fetchPersonalizationProductByHandle() {
    const handles = [
      'personalisation',
      'personalization',
      'personalisation-1',
      'boot-personalisation',
      'boot-personalization',
      'add-personalization'
    ];

    for (const handle of handles) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          this.personalizationProduct = await response.json();
          this.updatePersonalizationUI();
          return;
        }
      } catch (e) {
        continue;
      }
    }
    console.warn('ProductAddons: Could not find personalization product');
  }

  /**
   * Update the UI with personalization product details
   */
  updatePersonalizationUI() {
    if (!this.personalizationProduct) return;

    const imageContainer = this.querySelector('[data-personalization-image]');
    const titleEl = this.querySelector('[data-personalization-title]');
    const priceEl = this.querySelector('[data-personalization-price]');

    // Update image and remove loading state
    if (imageContainer) {
      imageContainer.classList.remove('product-addon__image--loading');

      if (this.personalizationProduct.featured_image) {
        const img = document.createElement('img');
        // Use Shopify's image URL format with size parameter
        img.src = this.getSizedImageUrl(this.personalizationProduct.featured_image, '120x120');
        img.alt = this.personalizationProduct.title || 'Personalization';
        img.width = 120;
        img.height = 120;
        img.loading = 'lazy';
        imageContainer.innerHTML = '';
        imageContainer.appendChild(img);
      } else {
        imageContainer.innerHTML = '';
      }
    }

    // Update title
    if (titleEl) {
      titleEl.textContent = this.personalizationProduct.title || 'Add Personalization';
    }

    // Update price and remove loading state
    if (priceEl) {
      priceEl.classList.remove('product-addon__price--loading');

      if (this.personalizationProduct.variants && this.personalizationProduct.variants.length > 0) {
        const variant = this.personalizationProduct.variants[0];
        this.personalizationVariantId = variant.id;

        // Store variant ID on checkbox
        if (this.personalizationCheckbox) {
          this.personalizationCheckbox.dataset.variantId = variant.id;
        }

        // Shopify .js endpoint always returns price in cents
        const price = variant.price / 100;
        priceEl.textContent = this.formatMoney(price);
      }
    }
  }

  /**
   * Get a sized image URL from Shopify CDN
   * Handles both simple URLs and URLs with query params
   * @param {string} url - Original image URL
   * @param {string} size - Size string (e.g., '120x120')
   * @returns {string} Sized image URL
   */
  getSizedImageUrl(url, size) {
    if (!url) return '';

    // Remove any existing size suffix and query params for clean base URL
    const urlObj = new URL(url, window.location.origin);
    const pathname = urlObj.pathname;

    // Match pattern: filename.ext or filename_100x100.ext
    const match = pathname.match(/^(.+?)(?:_\d+x\d+)?(\.[^.]+)$/);
    if (match) {
      urlObj.pathname = `${match[1]}_${size}${match[2]}`;
      return urlObj.toString();
    }

    // Fallback: just append size parameter
    return url;
  }

  /**
   * Format money according to Shopify's money format
   * @param {number} cents - Price in cents or dollars
   * @returns {string} Formatted price
   */
  formatMoney(amount) {
    // Try to use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(amount * 100, window.theme?.moneyFormat || '${{amount}}');
    }

    // Fallback formatting
    const formatter = new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    });
    return formatter.format(amount);
  }

  /**
   * Toggle personalization details visibility
   */
  handlePersonalizationToggle() {
    const isChecked = this.personalizationCheckbox?.checked || false;

    // Show/hide the personalization details section
    if (this.personalizationDetails) {
      this.personalizationDetails.hidden = !isChecked;
    }

    // Clear inputs and errors when unchecked
    if (!isChecked) {
      if (this.leftBootInput) this.leftBootInput.value = '';
      if (this.rightBootInput) this.rightBootInput.value = '';
      this.hidePersonalizationError();
    }

    this.updateFormValidity();
  }

  /**
   * Handle gender selection change
   * @param {Event} event
   */
  handleGenderChange(event) {
    this.selectedGender = event.target.value;
    this.selectedSize = null;
    this.renderSizeOptions();

    // Show size options container
    if (this.sizeOptionsContainer) {
      this.sizeOptionsContainer.hidden = false;
    }

    // Clear any existing error
    this.hideSizeError();

    // Update form validity (still invalid - size not selected yet)
    this.updateFormValidity();

    this.syncFormProperties();
  }

  /**
   * Render size options based on selected gender
   */
  renderSizeOptions() {
    if (!this.sizeGrid || !this.selectedGender) return;

    const sizes = ProductAddons.SIZES[this.selectedGender] || [];
    this.sizeGrid.innerHTML = '';

    sizes.forEach((size, index) => {
      const label = document.createElement('label');
      label.className = 'product-addon__size-option';

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'shoe_size';
      radio.value = `EU ${size.eu} - UK ${size.uk}`;
      radio.className = 'product-addon__size-radio';
      radio.dataset.testid = `size-${size.eu}`;
      radio.addEventListener('change', () => this.handleSizeChange(size));

      const span = document.createElement('span');
      span.className = 'product-addon__size-label';
      if (size.customOrder) {
        span.classList.add('product-addon__size-label--custom-order');
      }

      span.innerHTML = `
        <span class="product-addon__size-label-eu">EU ${size.eu}</span>
        <span class="product-addon__size-label-uk">UK ${size.uk}</span>
      `;

      label.appendChild(radio);
      label.appendChild(span);
      this.sizeGrid.appendChild(label);
    });
  }

  /**
   * Handle size selection change
   * @param {Object} size - The selected size object
   */
  handleSizeChange(size) {
    this.selectedSize = size;
    // Clear any existing error
    this.hideSizeError();
    // Update form validity (now valid - both gender and size selected)
    this.updateFormValidity();

    this.syncFormProperties();
  }

  /**
   * Get the line item properties for the main product
   * @returns {Record<string, string>}
   */
  getMainProductProperties() {
    const properties = {};

    if (this.selectedGender) {
      properties['Gender'] = this.selectedGender;
    }

    if (this.selectedSize) {
      properties['Size'] = `EU ${this.selectedSize.eu} - UK ${this.selectedSize.uk}`;
      if (this.selectedSize.customOrder) {
        properties['Custom Order'] = 'Yes';
      }
    }

    return properties;
  }

  /**
   * Validate that size selection is complete
   * @returns {boolean} true if valid, false if invalid
   */
  validateSizeSelection() {
    // Check if gender is selected
    if (!this.selectedGender) {
      this.showSizeError('Please select your gender');
      return false;
    }

    // Check if size is selected
    if (!this.selectedSize) {
      this.showSizeError('Please select your shoe size');
      return false;
    }

    this.hideSizeError();
    return true;
  }

  /**
   * Show size validation error
   * @param {string} message
   */
  showSizeError(message) {
    // Open the size accordion if closed so user can see what to fix
    const sizeAccordion = this.querySelector('[data-testid="size-accordion"]');
    if (sizeAccordion && !sizeAccordion.open) {
      sizeAccordion.open = true;
    }

    if (this.sizeError) {
      this.sizeError.hidden = false;
      if (this.sizeErrorMessage) {
        this.sizeErrorMessage.textContent = message;
      }
      // Scroll to error
      this.sizeError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Hide size validation error
   */
  hideSizeError() {
    if (this.sizeError) {
      this.sizeError.hidden = true;
    }
  }

  /**
   * Validate that personalization text inputs are filled when personalization is enabled
   * @returns {boolean} true if valid, false if invalid
   */
  validatePersonalization() {
    if (!this.personalizationCheckbox?.checked) {
      this.hidePersonalizationError();
      return true;
    }

    const leftText = this.leftBootInput?.value?.trim() || '';
    const rightText = this.rightBootInput?.value?.trim() || '';

    if (!leftText || !rightText) {
      const missing = !leftText && !rightText
        ? 'left and right boot personalization'
        : !leftText
          ? 'left boot personalization'
          : 'right boot personalization';
      this.showPersonalizationError(`Please enter ${missing}`);
      return false;
    }

    this.hidePersonalizationError();
    return true;
  }

  /**
   * Show personalization validation error
   * @param {string} message
   */
  showPersonalizationError(message) {
    // Open the personalization accordion if closed so user can see what to fix
    const personalizationAccordion = this.querySelector('[data-testid="personalization-accordion"]');
    if (personalizationAccordion && !personalizationAccordion.open) {
      personalizationAccordion.open = true;
    }

    // Add error styling to empty inputs
    if (this.leftBootInput && !this.leftBootInput.value.trim()) {
      this.leftBootInput.classList.add('product-addon__text-input--error');
    }
    if (this.rightBootInput && !this.rightBootInput.value.trim()) {
      this.rightBootInput.classList.add('product-addon__text-input--error');
    }

    if (this.personalizationError) {
      this.personalizationError.hidden = false;
      if (this.personalizationErrorMessage) {
        this.personalizationErrorMessage.textContent = message;
      }
      this.personalizationError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Hide personalization validation error
   */
  hidePersonalizationError() {
    if (this.personalizationError) {
      this.personalizationError.hidden = true;
    }
    // Remove error styling from inputs
    this.leftBootInput?.classList.remove('product-addon__text-input--error');
    this.rightBootInput?.classList.remove('product-addon__text-input--error');
  }

  /**
   * Update form validity state based on current selection
   * This keeps the hidden input's validity in sync with selection state,
   * which prevents the theme's fly-to-cart animation when form is invalid
   */
  updateFormValidity() {
    let invalid = !this.selectedGender || !this.selectedSize;
    let message = invalid ? 'Please select your size' : '';

    if (!invalid && this.personalizationCheckbox?.checked) {
      const leftText = this.leftBootInput?.value?.trim() || '';
      const rightText = this.rightBootInput?.value?.trim() || '';
      if (!leftText || !rightText) {
        invalid = true;
        message = 'Please enter both boot personalization texts';
      }
    }

    if (this.sizeValidationInput) {
      this.sizeValidationInput.setCustomValidity(message);
    }

    if (this.formValidationInput) {
      this.formValidationInput.value = invalid ? '' : '1';
      this.formValidationInput.setCustomValidity(message);
    }
  }

  /**
   * Ensure hidden form inputs exist for validation and properties
   */
  ensureFormInputs() {
    if (!this.form) return;

    let validationInput = this.form.querySelector('input[name="size_validation"][data-size-validation]');
    if (!validationInput) {
      validationInput = document.createElement('input');
      validationInput.type = 'text';
      validationInput.name = 'size_validation';
      validationInput.required = true;
      validationInput.className = 'visually-hidden';
      validationInput.setAttribute('aria-hidden', 'true');
      validationInput.setAttribute('tabindex', '-1');
      validationInput.setAttribute('data-size-validation', '');
      validationInput.addEventListener('invalid', this.boundHandleValidationInvalid);
      this.form.appendChild(validationInput);
    }
    this.formValidationInput = validationInput;

    const ensurePropertyInput = (name, key) => {
      let input = this.form.querySelector(`input[name="${name}"][data-product-addon-property="${key}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-product-addon-property', key);
        this.form.appendChild(input);
      }
      return input;
    };

    this.formPropertyInputs.gender = ensurePropertyInput('properties[Gender]', 'gender');
    this.formPropertyInputs.size = ensurePropertyInput('properties[Size]', 'size');
    this.formPropertyInputs.customOrder = ensurePropertyInput('properties[Custom Order]', 'custom-order');
  }

  /**
   * Sync property inputs in the form with the current selection
   */
  syncFormProperties() {
    if (!this.form) return;

    this.ensureFormInputs();

    const properties = this.getMainProductProperties();
    const hasGender = Boolean(properties['Gender']);
    const hasSize = Boolean(properties['Size']);
    const hasCustomOrder = Boolean(properties['Custom Order']);

    if (this.formPropertyInputs.gender) {
      this.formPropertyInputs.gender.value = properties['Gender'] || '';
      this.formPropertyInputs.gender.disabled = !hasGender;
    }

    if (this.formPropertyInputs.size) {
      this.formPropertyInputs.size.value = properties['Size'] || '';
      this.formPropertyInputs.size.disabled = !hasSize;
    }

    if (this.formPropertyInputs.customOrder) {
      this.formPropertyInputs.customOrder.value = properties['Custom Order'] || '';
      this.formPropertyInputs.customOrder.disabled = !hasCustomOrder;
    }
  }

  /**
   * Handle native validation invalid events for size requirements
   * @param {Event} event
   */
  handleValidationInvalid(event) {
    event.preventDefault();
    this.validateSizeSelection();
    this.validatePersonalization();
  }

  /**
   * Show cart error message
   * @param {string} message
   */
  showCartError(message) {
    if (this.cartError) {
      this.cartError.hidden = false;
      if (this.cartErrorMessage) {
        this.cartErrorMessage.textContent = message;
      }
      this.cartError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Hide cart error message
   */
  hideCartError() {
    if (this.cartError) {
      this.cartError.hidden = true;
    }
  }

  /**
   * Handle form submission to add addon items to cart
   * @param {Event} event
   */
  async handleFormSubmit(event) {
    console.log('[ProductAddons] handleFormSubmit triggered');

    if (this.productForm && !this.productForm.contains(event.target)) {
      return;
    }

    // Hide any previous cart errors
    this.hideCartError();

    // Validate size selection is required
    if (!this.validateSizeSelection()) {
      console.log('[ProductAddons] Size validation failed - stopping submission');
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // Validate personalization text inputs when personalization is toggled on
    if (!this.validatePersonalization()) {
      console.log('[ProductAddons] Personalization validation failed - stopping submission');
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    this.syncFormProperties();

    const addonItems = this.getAddonItems();
    if (addonItems.length > 0) {
      const form = event.target.closest('form');
      const variantInput = form?.querySelector('[name="id"]');
      if (!variantInput?.value) {
        this.showCartError('Could not determine product variant. Please refresh and try again.');
        return;
      }
      this.pendingAddonItems = addonItems;
      this.pendingAddonVariantId = variantInput?.value || null;

      if (this.pendingAddonTimeout) {
        clearTimeout(this.pendingAddonTimeout);
      }
      this.pendingAddonTimeout = setTimeout(() => {
        this.pendingAddonItems = null;
        this.pendingAddonVariantId = null;
        this.pendingAddonTimeout = null;
      }, 5000);
    }
  }

  /**
   * Handle successful cart addition
   */
  handleCartSuccess() {
    // Dispatch cart update event
    document.dispatchEvent(new CustomEvent('cart:updated'));

    // Try to open cart drawer if available
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      // Fallback: refresh cart count or redirect
      const cartIcon = document.querySelector('[data-cart-count]');
      if (cartIcon) {
        // Fetch and update cart count
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            cartIcon.textContent = cart.item_count;
          });
      }

      // Optionally redirect to cart
      // window.location.href = '/cart';
    }
  }

  /**
   * Handle cart updates to append addon items after main product add
   * @param {Event} event
   */
  handleCartUpdate(event) {
    if (!this.pendingAddonItems || this.isAddingAddons) return;

    const source = event?.detail?.data?.source;
    if (source !== 'product-form-component') return;

    if (this.pendingAddonVariantId && event?.detail?.sourceId) {
      if (this.pendingAddonVariantId.toString() !== event.detail.sourceId.toString()) {
        return;
      }
    }

    const addonItems = this.pendingAddonItems;
    this.pendingAddonItems = null;
    this.pendingAddonVariantId = null;
    if (this.pendingAddonTimeout) {
      clearTimeout(this.pendingAddonTimeout);
      this.pendingAddonTimeout = null;
    }

    this.isAddingAddons = true;
    this.addItemsToCart(addonItems)
      .catch((error) => {
        console.error('[ProductAddons] Failed to add addon items:', error);
        this.showCartError('Failed to add addon items. Please try again.');
      })
      .finally(() => {
        this.isAddingAddons = false;
      });
  }

  /**
   * Re-ensure form bindings and inputs after variant update morphs the form
   */
  handleVariantUpdate() {
    this.tryBindToProductForm();
    this.ensureFormInputs();
    this.syncFormProperties();
    this.updateFormValidity();
  }

  /**
   * Get the addon items that should be added to cart
   * @returns {Array<{id: number, quantity: number, properties?: Record<string, string>}>}
   */
  getAddonItems() {
    const items = [];

    // Check addon checkbox
    if (this.addonCheckbox?.checked) {
      const variantId = this.addonCheckbox.dataset.variantId;
      if (variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: 1
        });
      }
    }

    // Check personalization checkbox
    if (this.personalizationCheckbox?.checked) {
      const variantId = this.personalizationCheckbox.dataset.variantId || this.personalizationVariantId;
      const leftText = this.leftBootInput?.value?.trim() || '';
      const rightText = this.rightBootInput?.value?.trim() || '';

      if (variantId) {
        const item = {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {}
        };

        if (leftText) {
          item.properties['Left Boot'] = leftText.toUpperCase();
        }
        if (rightText) {
          item.properties['Right Boot'] = rightText.toUpperCase();
        }

        items.push(item);
      }
    }

    return items;
  }

  /**
   * Add items to cart via Shopify Cart API
   * @param {Array<{id: number, quantity: number, properties?: Record<string, string>}>} items
   */
  async addItemsToCart(items) {
    // Use Shopify's routes for multi-language support
    const cartAddUrl = (window.Shopify?.routes?.root || '/') + 'cart/add.js';
    const requestBody = { items };

    console.log('[ProductAddons] Cart API URL:', cartAddUrl);
    console.log('[ProductAddons] Request body:', JSON.stringify(requestBody, null, 2));

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ProductAddons] Cart API error response:', errorText);
        throw new Error('Failed to add items to cart');
      }

      const result = await response.json();
      console.log('[ProductAddons] Cart API success response:', result);

      // Dispatch cart update event for other components to react
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { items: result.items }
      }));

      const addonItemCount = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const cartUpdateEvent = typeof ThemeEvents !== 'undefined' ? ThemeEvents.cartUpdate : 'cart:update';
      document.dispatchEvent(new CustomEvent(cartUpdateEvent, {
        detail: {
          data: {
            source: 'product-form-component',
            itemCount: addonItemCount
          }
        }
      }));

      return result;
    } catch (error) {
      console.error('ProductAddons: Failed to add items to cart', error);
      throw error;
    }
  }
}

// Register the custom element
if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddons);
}
